import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// HERO full-bleed image — "molto impattante"
const HERO_IMAGE =
  "/images/assets/r96ciop1_main-photo.jpg";

// SEZ. 6 — I 3 prodotti (Train · Empower · Repeat).
// Mapping 1:1: prodotti con foto disponibile vengono renderizzati; gli altri
// mostrano placeholder "Product visual in arrivo".
const PRODOTTI = [
  {
    label: "Pre-workout",
    titolo: "TRAIN",
    desc:
      "La preparazione del corpo prima dell'attività. Per arrivare all'allenamento con il sistema giusto in posizione.",
    img: "/images/assets/mxuuv5xm_20221202_14-37-57_z62_6852.jpg",
    alt: "Empower — Unguento Prodigioso · Pre-workout su sfondo giallo con bilanciere e kettlebell",
  },
  {
    label: "Performance",
    titolo: "EMPOWER",
    desc:
      "Il prodotto del momento attivo. Sostegno mirato per le parti del corpo più sollecitate durante lo sforzo.",
    img: "/images/assets/99z2vz3n_foto-enpower.jpg",
    alt: "Empower — Gel Artiglio del Diavolo · Packaging Em.Power su scena viola con foam roller, plates e bande di resistenza",
  },
  {
    label: "Post-workout · Recovery",
    titolo: "REPEAT",
    desc:
      "Il recupero che permette di ripetere l'allenamento successivo nelle migliori condizioni. La parte invisibile del ciclo.",
    img: "/images/assets/fo8c1cg5_20221202_15-24-37_z62_6876.jpg",
    alt: "Empower — Gel Arnica Suprema 98 · Performance Pack su fondo nero con bande di resistenza gialle",
  },
];

// SEZ. 8 — Galleria visuale (8 slot, layout magazine 4-righe).
// Anti-duplicazione totale: niente riuso di HERO o PRODOTTI.
const GALLERY_SLOTS = 8;
const GALLERY_PHOTOS = [
  // gallery-01 — Modello "Practice Recovery" su atleta donna in lilla, mare sullo sfondo
  "/images/assets/edo08qp8_artboard-copy-18-980x980.jpg",
  // gallery-02 — "Enjoy the Effort" — uomo over con racchetta tennis, claim editoriale lime
  "/images/assets/qif0ypyb_artboard-copy-16-768x768.png",
  // gallery-03 — Composizione 5-prodotti "Practice Recovery" su sfondo blu
  "/images/assets/mje9ub1c_artboard-copy-24-1-scaled.jpg",
  // gallery-04 — Gel Artiglio del Diavolo su scena viola, plates e bande di resistenza
  "/images/assets/gaz0qlcf_20221202_16-26-22_z62_6883.jpg",
  // gallery-05 — Poster "Artiglio del Diavolo · Contrasta i dolori muscolari" con drago lineare (full-width)
  "/images/assets/5i9puej9_artboard-copy-3.jpg",
  // gallery-06 — Type specimen: TheAliens + N27 (alfabeto e numeri su fondo blu)
  "/images/assets/649zp747_artboard-copy-17.png",
  // gallery-07 — Poster "Your body is a temple · Never forget to rest" + logo EM.POWER (fondo giallo lime)
  "/images/assets/hdxfnbl1_artboard-copy-2.jpg",
  // gallery-08 — Gel Agilis su sfondo blu con kettlebell e bande arancioni
  "/images/assets/kyhady63_20221202_15-00-43_z62_6862.jpg",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Empower — Brand & visual identity · Asset ${String(i + 1).padStart(2, "0")} di ${GALLERY_SLOTS}`,
}));

// Layout magazine 8 slot: 1 full · 2-4 quarter · 5 full · 6-8 quarter
const SLOT_LAYOUT = [
  "col-span-4 md:col-span-12",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-12",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
];

const WorkEmpower = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  const idx = WORKS.findIndex((w) => w.to === "/works/empower");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = idx >= 0 ? WORKS[(idx + 1) % WORKS.length] : WORKS[0];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Empower", url: "/works/empower" },
  ]);

  return (
    <>
      <Seo
        title="Empower Biolife: 1.000 kit primo mese, ROAS 236% | OnSports Case"
        description="Lancio Empower Biolife: 1.000+ kit venduti il primo mese, ROAS 236% su Meta Ads. Performance marketing OnSports per il brand wellness. 2025."
        path="/works/empower"
        image="/images/assets/r96ciop1_main-photo.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/empower/hero.jpg"
        imageAlt="Empower · Biolife · Recovery Line"
        eyebrow="● Case study · Business Impact · Brand Creation · E-commerce"
        titleLines={["Train. Empower.", "REPEAT."]}
        highlightWord="REPEAT."
        subtitle="Biolife ci ha chiesto di costruire un brand da zero per la sua nuova linea di prodotti pre e post workout dedicata al recovery degli atleti. Abbiamo gestito naming, concept, identity, packaging, photo shooting, sales page e performance marketing. Risultato: oltre 1.000 kit venduti nel primo mese con ROAS 236% tra email marketing e advertising."
        meta="Cliente — Biolife  ·  Linea — Empower  ·  Brand Creation & E-commerce · End-to-end"
      />

      {/* SEZ. 2 — HERO IMAGE FULL-BLEED */}
      <section className="bg-dodgers" data-testid="section-hero-image">
        <RevealOnScroll>
          {HERO_IMAGE ? (
            <img
              src={HERO_IMAGE}
              alt="Empower — La nuova linea di recovery sportivo costruita per Biolife"
              className="block w-screen object-cover"
              style={{ height: "clamp(50vh, 70vh, 80vh)", objectPosition: "center" }}
              loading="lazy"
              draggable="false"
              data-testid="hero-image-full"
            />
          ) : (
            <div
              className="w-screen bg-dodgers border-y border-lime/30 flex items-center justify-center"
              style={{ height: "clamp(50vh, 70vh, 80vh)" }}
              data-testid="hero-image-placeholder"
            >
              <span className="font-condensed uppercase tracking-widest text-[14px] text-galactico">
                Hero image in arrivo
              </span>
            </div>
          )}
        </RevealOnScroll>
        <div className="container-os pt-4 pb-12 md:pb-16">
          <p
            className="text-[13px] leading-[1.55] text-mandrogni opacity-80"
            style={{ fontStyle: "italic" }}
          >
            Empower — La nuova linea di recovery sportivo costruita per Biolife.
          </p>
        </div>
      </section>

      {/* SEZ. 3 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Biolife è un'azienda italiana del settore cosmetico con
                    un'expertise consolidata nella produzione di formulazioni
                    dermatologicamente testate. Nel 2024 ha deciso di estendere
                    la propria offerta verso il mercato dello sport e del
                    fitness, sviluppando una linea di prodotti pensati per
                    supportare l'atleta nelle fasi di preparazione, esecuzione
                    e recupero dell'attività fisica. È un mercato in crescita —
                    quello del sport beauty &amp; recovery — in cui il
                    consumatore italiano sta diventando sempre più esigente in
                    termini di qualità, narrazione e performance del prodotto.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Quando Biolife ci ha contattati, aveva chiaro il prodotto:
                    tre formulazioni — un pre-workout, un performance e un
                    post-workout per il recovery — già validate dal proprio
                    laboratorio. Mancava tutto il resto. Mancava il brand.
                    Mancava il nome. Mancava il claim, l'identità visiva, il
                    packaging, il photo shooting, il sito di vendita e la
                    macchina commerciale per portarlo sul mercato. Il brief era
                    esattamente questo: costruisci tutto, e portalo a generare
                    vendite reali.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.25}>
                  <p>
                    Abbiamo lavorato end-to-end. Naming, concept, claim, brand
                    identity completa, design del packaging, photo shooting
                    professionale, sviluppo dell'e-commerce con sales page
                    costruita attorno al bundle dei tre prodotti, email
                    marketing e campagne advertising. Risultato dopo il primo
                    mese: oltre 1.000 kit venduti con un ROAS del 236% sui
                    canali paid e owned. Train. Empower. Repeat. — il claim del
                    brand è diventato anche la formula commerciale.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Brand">Empower</SchedaRow>
                    <SchedaRow label="Cliente">Biolife</SchedaRow>
                    <SchedaRow label="Prodotto">
                      Linea recovery sportivo (3 prodotti)
                    </SchedaRow>
                    <SchedaRow label="Categoria">
                      Sport Beauty &amp; Recovery
                    </SchedaRow>
                    <SchedaRow label="Claim">Train. Empower. Repeat.</SchedaRow>
                    <SchedaRow label="Payoff">Empower your sport.</SchedaRow>
                    <SchedaRow label="Ruolo">
                      Naming · Concept · Claim<br />
                      Brand identity · Packaging<br />
                      Photo shooting · E-commerce<br />
                      Sales page · Email &amp; Meta Ads
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Business Impact · Brand &amp; Product<br />
                      Performance Marketing &amp; Growth
                    </SchedaRow>
                    <SchedaRow label="Anno">2024</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Far esistere un brand sport dove prima c'era solo
                  formulazione.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il mercato del recovery sportivo italiano è ancora un
                  territorio in costruzione, ma è popolato da brand già
                  strutturati — alcuni nativi sport (Enervit, Volchem), altri
                  provenienti dal mondo cosmetico (Caudalie Sport, varie linee
                  Vichy e Bioderma), altri ancora dal canale farmaceutico. Un
                  brand nuovo, lanciato da un'azienda che il mercato sport non
                  conosceva, doveva conquistare visibilità e credibilità in un
                  panorama saturato dalla concorrenza.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  La sfida operativa era duplice. Da un lato, costruire un
                  brand sport credibile a partire da una matrice cosmetica —
                  codici visivi, tone of voice e linguaggio di prodotto che
                  parlassero la cultura sport-fitness italiana senza tradire la
                  radice qualitativa di Biolife. Dall'altro, portare il brand
                  al risultato commerciale nei primi 30 giorni di lancio: non
                  c'era margine per esercizi di pure brand awareness senza
                  misurabilità — Biolife voleva vedere ritorno economico, ROAS
                  positivo, paia di kit nel carrello degli atleti.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — IL BRAND */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-brand">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>Il brand</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Train. Empower. Repeat.</h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il naming è arrivato dalla sintesi del concept stesso:
                  Empower — verbo inglese che significa "dare potere", "rendere
                  capaci". Più del nome, contiene la promessa funzionale del
                  prodotto: questi tre articoli ti rendono capace di andare
                  oltre i limiti, allenamento dopo allenamento. È un
                  brand-verbo, non un brand-sostantivo, e questo lo rende
                  attivabile in modo naturale dentro ogni claim, ogni payoff,
                  ogni piece di copy commerciale.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Il claim Train. Empower. Repeat. segue il ciclo
                  dell'allenamento sportivo (preparazione → esecuzione →
                  ripetizione) e si presta a essere usato sia come messaggio
                  del brand che come schema mentale che il consumatore può
                  adottare. Il payoff Empower your sport allarga il messaggio
                  dal prodotto al gesto, dichiarando che il brand non vende
                  formulazioni — vende capacità di performance.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  La brand identity visiva è stata costruita su un sistema di
                  design contemporaneo, bold, con un linguaggio tipografico
                  forte e una palette chiara, capace di dichiarare
                  immediatamente l'appartenenza al mondo sport. Niente codici
                  cosmetic-soft, niente delicate skincare aesthetic — Empower è
                  entrato sul mercato come un brand sport contemporaneo, con la
                  propria voce.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          {/* Logo animation video */}
          <RevealOnScroll delay={0.1}>
            <div className="mt-12 md:mt-16 max-w-[1100px] mx-auto" data-testid="logo-animation">
              <div className="overflow-hidden shadow-2xl">
                <WistiaPlayer
                  mediaId="rqnww5vng0"
                  aspect={1.7777777777777777}
                  autoplay
                  loop
                  muted
                />
              </div>
              <p
                className="mt-4 text-mandrogni text-sm text-center"
                style={{ fontFamily: "'Hanken Grotesk', sans-serif", fontStyle: "italic" }}
              >
                Empower — Logo animation. La brand identity in movimento.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — I 3 PRODOTTI */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-prodotti">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>I prodotti</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Tre formulazioni. Un ciclo completo.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  La linea Empower è costruita attorno al ciclo
                  dell'allenamento: pre, performance, post. Ogni formulazione è
                  pensata per una fase specifica del gesto sportivo, e tutte e
                  tre insieme formano il kit Empower — il bundle che abbiamo
                  posizionato come prodotto di entry del brand sull'e-commerce.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {PRODOTTI.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.06}>
                <div
                  className="bg-galactico/[0.05] border border-lime p-6"
                  data-testid={`prodotto-${i}`}
                >
                  <div
                    className="block w-full overflow-hidden bg-dodgers/[0.04] mb-5"
                    style={{ aspectRatio: "4 / 5" }}
                  >
                    {p.img ? (
                      <img
                        src={p.img}
                        alt={p.alt}
                        loading="lazy"
                        className="block w-full h-full object-cover"
                        draggable="false"
                      />
                    ) : (
                      <div
                        className="w-full h-full bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                        data-testid={`prodotto-placeholder-${i}`}
                      >
                        <span
                          className="font-condensed uppercase tracking-widest text-galactico"
                          style={{ fontSize: "12px" }}
                        >
                          Product visual in arrivo
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="block font-condensed uppercase tracking-widest text-lime text-[11px]">
                    {p.label}
                  </span>
                  <h3
                    className="mt-2 font-display leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(24px, 3vw, 32px)" }}
                  >
                    {p.titolo}
                  </h3>
                  <p className="mt-3 text-[14px] leading-[1.55] opacity-85">
                    {p.desc}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 7 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Quattro filiere. Una sola regia.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Costruire un brand DTC end-to-end significa tenere insieme
                  filiere normalmente gestite da agenzie diverse: branding,
                  design, e-commerce, performance. Empower è un esercizio di
                  integrazione interna — abbiamo applicato il modello
                  multi-disciplinare che proponiamo ai clienti su un progetto
                  in cui Biolife ci ha affidato l'intera filiera senza handoff
                  intermedi.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Naming & concept"
                body="Sviluppo del naming Empower a partire dal posizionamento richiesto, costruzione del concept Train. Empower. Repeat. come ciclo dell'allenamento sportivo, definizione del payoff Empower your sport. Allineamento tra cosa il prodotto fa (cosmetico-funzionale) e cosa il brand dice (sport-empowerment)."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Brand identity & packaging"
                body="Design del logo, palette, tipografia, tone of voice. Costruzione di un design system contemporaneo, bold, riconoscibile sullo scaffale fisico e digital. Design del packaging dei tre prodotti coordinato con la brand identity — coerenza tra immagine commerciale del brand e oggetto fisico che il consumatore tiene in mano."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Photo shooting & visual assets"
                body="Photo shooting professionale del prodotto e dei testimonial, costruzione del catalogo visivo per e-commerce, social e materiali ADV. Sistema di visual riusabile in tutte le piattaforme — dal sito alla campagna Meta, dal post Instagram all'email transazionale."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="E-commerce, email & performance"
                body="Sviluppo della sales page e dell'e-commerce, integrazione del checkout, set up del flusso di email marketing (welcome, abandoned cart, post-purchase). Pianificazione e attivazione delle campagne Meta Ads — prospecting + retargeting — tarate sul target sport-fitness italiano. ROAS misurato 236% nel primo mese di lancio."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 9 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il brand in dettaglio.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Una selezione del lavoro di brand identity, packaging e photo
                  shooting prodotto.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-4 md:grid-cols-12 gap-3"
            data-testid="gallery-grid"
          >
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              const wrapperClass = SLOT_LAYOUT[i];
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll
                    key={i}
                    delay={Math.min(i, 5) * 0.04}
                    className={wrapperClass}
                  >
                    <div
                      className="block w-full relative bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                      style={{ aspectRatio: "4 / 3" }}
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Asset ${i + 1} di ${GALLERY_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Visual in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 5) * 0.04}
                  className={wrapperClass}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full overflow-hidden relative group focus:outline-none bg-dodgers/[0.04]"
                    style={{ aspectRatio: "4 / 3" }}
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri asset ${i + 1} di ${GALLERY_SLOTS}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 9 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Brand da zero. Vendite reali nel primo mese.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="1.000+" label="Kit venduti nel primo mese dal lancio" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="236%" label="ROAS · Email marketing + Meta advertising" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="3" label="Prodotti sviluppati · Pre · Performance · Post recovery" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="1" label="Brand creato da zero end-to-end" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati commerciali e di campagna Empower nel primo mese dal lancio
            dell'e-commerce. Brand sviluppato da OnSports per Biolife —
            naming, identity, packaging, sales page, email marketing e Meta
            Ads sotto un'unica regia.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Brand identity con metrica commerciale
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Empower dimostra che brand identity e performance marketing
                  non sono filiere separate. Quando la voce del brand, la
                  sales page e la campagna Meta sono progettate insieme, dal
                  primo giorno, il ROAS smette di essere una metrica "media"
                  e diventa il risultato naturale di una filiera che parla la
                  stessa lingua a tutti i livelli.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Il bundle come strategia di entry
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Posizionare il bundle dei tre prodotti come prodotto-faro
                  della sales page — invece di vendere prodotti singoli — ha
                  alzato il valore medio del carrello dal primo giorno e ha
                  permesso a Empower di entrare sul mercato con un AOV
                  (average order value) superiore alla media del segmento. È
                  una scelta strategica che si fa il giorno zero della
                  costruzione del sito, non dopo.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Da formulazione a brand sport
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Il valore strategico del progetto, oltre i numeri, è la
                  trasformazione di un'azienda cosmetica in un brand sport
                  credibile. Empower ha aperto a Biolife un canale nuovo —
                  quello dell'atleta consumer — su cui costruire estensioni
                  future di catalogo e nuove categorie di prodotto. Il brand
                  creato dura oltre il primo mese di lancio.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 10 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Brand">Empower</SchedaRow>
                <SchedaRow label="Cliente">Biolife</SchedaRow>
                <SchedaRow label="Prodotto">
                  Linea recovery sportivo (3 prodotti)
                </SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Naming · Concept · Claim · Identity<br />
                  Packaging · Photo shooting<br />
                  E-commerce · Sales page<br />
                  Email marketing · Meta Ads
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Brand &amp; Product Strategy · Content &amp; IP Studio<br />
                  Performance Marketing &amp; Growth
                </SchedaRow>
                <SchedaRow label="Tipologia">
                  Brand creation end-to-end per cliente
                </SchedaRow>
                <SchedaRow label="Anno">2024</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 11 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai un prodotto pronto ma ti manca tutto il resto? Costruiamo
              insieme il suo <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Naming, concept, identity, packaging, sales page, performance
              marketing — sotto un'unica regia. Quello che abbiamo fatto per
              Biolife con Empower possiamo farlo con il tuo brand. Risultato
              commerciale tracciabile dal primo mese, sotto KPI condivisi
              prima del lancio.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 12 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkEmpower;
